import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import { FaCalendarAlt, FaClock, FaCheckCircle, FaTimesCircle, FaBell } from 'react-icons/fa';

import { 
    COLORS, 
    PanelContent, 
    PanelTitle 
} from './styles';

/**
 * UpcomingSessions Component
 * 
 * Displays upcoming calendar sessions and reminders
 * Features:
 * - Toggle between agenda and day views
 * - Set reminders for events
 * - Auto-refreshes to keep the view updated
 */
const UpcomingSessions = () => {
    const { getAccessTokenSilently } = useAuth0();
    const authenticatedAxios = useAuthenticatedAxios();
    const [calendarData, setCalendarData] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
    }));
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [activeTab, setActiveTab] = useState('agenda'); // 'day' or 'agenda'
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showReminderPopup, setShowReminderPopup] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [reminderSet, setReminderSet] = useState({});
    const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
    const [isLoading, setIsLoading] = useState(true);
    
    // Function to format time from Date objects
    const formatEventTime = (startTime, endTime) => {
        const startDate = new Date(startTime);
        const endDate = new Date(endTime);
        
        // Format options
        const timeOptions = { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true 
        };
        
        return `${startDate.toLocaleTimeString('en-US', timeOptions)} - ${endDate.toLocaleTimeString('en-US', timeOptions)}`;
    };
    
    // Format event date in a compact way
    const formatEventDate = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        // Check if it's today or tomorrow
        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === tomorrow.toDateString()) {
            return 'Tomorrow';
        } else {
            // For other dates, return formatted date
            return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                weekday: 'short' 
            });
        }
    };
    
    // Check if meeting is ready (within 15 minutes of start time)
    const isMeetingReady = (startTime) => {
        const now = new Date();
        const meetingStart = new Date(startTime);
        const diffInMinutes = (meetingStart - now) / (1000 * 60);
        
        // Return true if meeting is within 15 minutes of starting (but hasn't started yet)
        return diffInMinutes > 0 && diffInMinutes <= 15;
    };
    
    // Check if meeting is finished (ended in the past)
    const isMeetingFinished = (endTime) => {
        const now = new Date();
        const meetingEnd = new Date(endTime);
        
        // Return true if the meeting has ended
        return now > meetingEnd;
    };
    
    // Check if a reminder is set for an event
    const hasReminder = (eventId) => {
        return reminderSet[eventId] !== undefined;
    };
    
    // Get reminder text for an event
    const getReminderText = (eventId) => {
        return reminderSet[eventId] || '';
    };

    // More reliable way to check if an event is still valid to display
    const isValidEvent = (event) => {
        if (!event) return false;
        
        try {
            // Basic validation - must have these fields
            if (!event.StartTime || !event.EndTime || !event.Id) {
                console.log("Invalid event missing required fields:", event);
                return false;
            }
            
            const endTime = new Date(event.EndTime);
            const now = new Date();
            
            // Only filter out events that ended more than 30 minutes ago
            // This provides a buffer to prevent disappearing events
            const endedTimeAgo = (now - endTime) / (1000 * 60);
            return endedTimeAgo < 30;
        } catch (error) {
            console.error("Error validating event:", error, event);
            return false;
        }
    };

    // Filter events to get upcoming events (agenda view)
    const getUpcomingEvents = useCallback((events) => {
        console.log("Refreshing agenda view with", events?.length || 0, "events");
        
        if (!events || !Array.isArray(events) || events.length === 0) {
            console.log("No events data to process");
            return [];
        }
        
        try {
            const now = new Date();
            
            // More robust filtering - only exclude events that ended more than 30 minutes ago
            const validEvents = events.filter(isValidEvent);
            console.log("Valid events count:", validEvents.length);
            
            // Sort by start time
            const sortedEvents = validEvents.sort((a, b) => {
                try {
                    const aStart = new Date(a.StartTime);
                    const bStart = new Date(b.StartTime);
                    return aStart - bStart;
                } catch (error) {
                    console.error("Error sorting events:", error);
                    return 0;
                }
            });
            
            // Filter out finished events for agenda view
            const filteredEvents = sortedEvents.filter(event => {
                try {
                    const endTime = new Date(event.EndTime);
                    return now <= endTime; // Only include events that haven't ended yet
                } catch (error) {
                    console.error("Error filtering finished events:", error, event);
                    return true; // Include event if there's an error to be safe
                }
            });
            
            // Take only first 10
            const limitedEvents = filteredEvents.slice(0, 10);
            
            // Map to the format we need for display
            const mappedEvents = limitedEvents.map((event, index) => {
                try {
                    const startTime = new Date(event.StartTime);
                    const endTime = new Date(event.EndTime);
                    const now = new Date();
                    
                    let status;
                    if (isMeetingFinished(endTime)) {
                        status = 'finished';
                    } else if (now >= startTime && now <= endTime) {
                        status = 'in-progress';
                    } else {
                        status = 'upcoming';
                    }
                    
                    return {
                        id: event.Id || index,
                        time: formatEventTime(event.StartTime, event.EndTime),
                        date: formatEventDate(event.StartTime),
                        title: event.Subject || 'Untitled Event',
                        client: event.Description || 'No client details',
                        location: event.Location || '',
                        status: status,
                        startTime: startTime,
                        endTime: endTime,
                        isReady: isMeetingReady(event.StartTime),
                        rawEvent: event // Keep the original event data for debugging
                    };
                } catch (error) {
                    console.error("Error mapping event:", error, event);
                    return null;
                }
            }).filter(e => e !== null); // Remove any events that failed mapping
            
            console.log("Final events count for display:", mappedEvents.length);
            return mappedEvents;
        } catch (error) {
            console.error("Error processing events:", error);
            return [];
        }
    }, []);
    
    // Get events for a specific day (day view)
    const getDayEvents = useCallback((events, date) => {
        console.log("Refreshing day view with", events?.length || 0, "events for date", date);
        
        if (!events || !Array.isArray(events) || events.length === 0 || !date) {
            console.log("No events data to process for day view");
            return [];
        }
        
        try {
            const selectedDay = new Date(date);
            // Reset the time part to compare only dates
            selectedDay.setHours(0, 0, 0, 0);
            
            const now = new Date();
            
            // Filter events for the selected day
            const dayEvents = events
                .filter(event => {
                    try {
                        if (!event || !event.StartTime) return false;
                        
                        const eventStart = new Date(event.StartTime);
                        // Check if the event starts on the selected day
                        return eventStart.getDate() === selectedDay.getDate() && 
                               eventStart.getMonth() === selectedDay.getMonth() &&
                               eventStart.getFullYear() === selectedDay.getFullYear();
                    } catch (error) {
                        console.error("Error filtering day events:", error, event);
                        return false;
                    }
                });
                
            console.log("Day events count:", dayEvents.length);
                
            // Sort by start time
            const sortedEvents = dayEvents.sort((a, b) => {
                try {
                    const aStart = new Date(a.StartTime);
                    const bStart = new Date(b.StartTime);
                    return aStart - bStart;
                } catch (error) {
                    console.error("Error sorting day events:", error);
                    return 0;
                }
            });
            
            // Map to the format we need for display
            const mappedEvents = sortedEvents.map((event, index) => {
                try {
                    const startTime = new Date(event.StartTime);
                    const endTime = new Date(event.EndTime);
                    const now = new Date();
                    
                    let status;
                    if (isMeetingFinished(endTime)) {
                        status = 'finished';
                    } else if (now >= startTime && now <= endTime) {
                        status = 'in-progress';
                    } else {
                        status = 'upcoming';
                    }
                    
                    return {
                        id: event.Id || index,
                        time: formatEventTime(event.StartTime, event.EndTime),
                        date: formatEventDate(event.StartTime),
                        title: event.Subject || 'Untitled Event',
                        client: event.Description || 'No client details',
                        location: event.Location || '',
                        status: status,
                        startTime: startTime,
                        endTime: endTime,
                        isReady: isMeetingReady(event.StartTime),
                        rawEvent: event // Keep the original event data for debugging
                    };
                } catch (error) {
                    console.error("Error mapping day event:", error, event);
                    return null;
                }
            }).filter(e => e !== null); // Remove any events that failed mapping
            
            console.log("Final day events count for display:", mappedEvents.length);
            return mappedEvents;
        } catch (error) {
            console.error("Error processing day events:", error);
            return [];
        }
    }, []);
    
    // Handle setting a reminder
    const handleSetReminder = (eventId, reminderTime) => {
        setReminderSet(prev => ({
            ...prev,
            [eventId]: reminderTime
        }));
        
        // Show a notification to confirm the reminder was set
        // In a real implementation, you would store this in a database and set up an actual reminder
        
        setShowReminderPopup(false);
    };
    
    // Open reminder popup for an event
    const openReminderPopup = (event) => {
        setSelectedEvent(event);
        setShowReminderPopup(true);
    };
    
    // Function to refresh event data
    const refreshEvents = useCallback(() => {
        console.log("Manually refreshing events");
        setLastRefreshTime(Date.now());
        
        if (activeTab === 'agenda') {
            setUpcomingEvents(getUpcomingEvents(calendarData));
        } else {
            setUpcomingEvents(getDayEvents(calendarData, selectedDate));
        }
    }, [calendarData, getUpcomingEvents, getDayEvents, activeTab, selectedDate]);

    // Initial fetch of calendar data
    useEffect(() => {
        let isMounted = true;
        const fetchCalendarData = async () => {
            setIsLoading(true);
            try {
                console.log("Fetching calendar data from API");
                const response = await authenticatedAxios.get('calander/GetCalanderData');
                
                if (!isMounted) return;
                
                if (response && response.data && Array.isArray(response.data)) {
                    console.log("Successfully fetched", response.data.length, "calendar events");
                    setCalendarData(response.data);
                    
                    // Update event list based on active tab
                    if (activeTab === 'agenda') {
                        const events = getUpcomingEvents(response.data);
                        setUpcomingEvents(events);
                    } else {
                        const events = getDayEvents(response.data, selectedDate);
                        setUpcomingEvents(events);
                    }
                } else {
                    console.error("Invalid calendar data format:", response);
                }
            } catch (error) {
                console.error("Error fetching calendar data:", error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchCalendarData();
        
        // Set up timer to refresh the current date and events list
        const timerID = setInterval(() => {
            console.log("Timer refresh triggered");
            const now = new Date();
            setCurrentDate(now);
            setCurrentTime(now.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
            }));
            
            if (!isMounted) return;
            
            // Only refresh events if it's been more than 55 seconds since last refresh
            // This prevents multiple concurrent refreshes
            if (Date.now() - lastRefreshTime > 55000) {
                setLastRefreshTime(Date.now());
                
                if (activeTab === 'agenda') {
                    const events = getUpcomingEvents(calendarData);
                    if (events.length > 0 || upcomingEvents.length === 0) {
                        setUpcomingEvents(events);
                    }
                } else {
                    const events = getDayEvents(calendarData, selectedDate);
                    if (events.length > 0 || upcomingEvents.length === 0) {
                        setUpcomingEvents(events);
                    }
                }
            }
        }, 60000); // Check every minute
        
        return () => {
            isMounted = false;
            clearInterval(timerID);
        };
    }, [authenticatedAxios, getUpcomingEvents, getDayEvents, activeTab, selectedDate, lastRefreshTime]);
    
    // Update the events list when calendarData changes or tab/date selection changes
    useEffect(() => {
        console.log("Tab or date selection changed, updating events");
        
        if (calendarData.length === 0) {
            console.log("No calendar data available yet");
            return;
        }
        
        if (activeTab === 'agenda') {
            const events = getUpcomingEvents(calendarData);
            setUpcomingEvents(events);
        } else {
            const events = getDayEvents(calendarData, selectedDate);
            setUpcomingEvents(events);
        }
    }, [calendarData, getUpcomingEvents, getDayEvents, activeTab, selectedDate]);
    
    // Handle tab change
    const handleTabChange = (tab) => {
        console.log("Changing tab to", tab);
        setActiveTab(tab);
        setShowDatePicker(false);
        setShowReminderPopup(false);
        
        // Force refresh events when tab changes
        if (tab === 'agenda') {
            setUpcomingEvents(getUpcomingEvents(calendarData));
        } else {
            setUpcomingEvents(getDayEvents(calendarData, selectedDate));
        }
    };
    
    // Handle date change
    const handleDateChange = (date) => {
        console.log("Changing selected date to", date);
        setSelectedDate(date);
        setShowDatePicker(false);
        
        // If we're in day view, refresh the events for the new date
        if (activeTab === 'day') {
            setUpcomingEvents(getDayEvents(calendarData, date));
        }
    };

    // Reminder Popup Component
    const ReminderPopup = () => {
        if (!selectedEvent) return null;
        
        const reminderOptions = [
            { text: '5 minutes before', value: '5 min' },
            { text: '15 minutes before', value: '15 min' },
            { text: '30 minutes before', value: '30 min' },
            { text: '1 hour before', value: '1 hour' },
            { text: '1 day before', value: '1 day' }
        ];
        
        return (
            <div style={{
                background: COLORS.bgTertiary,
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                padding: '18px',
                position: 'absolute',
                zIndex: 10,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '300px',
                border: `1px solid ${COLORS.border}`
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                }}>
                    <div style={{ color: COLORS.light, fontSize: '16px', fontWeight: '600' }}>
                        Set Reminder
                    </div>
                    <button 
                        onClick={() => setShowReminderPopup(false)}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: COLORS.muted,
                            cursor: 'pointer',
                            fontSize: '18px'
                        }}
                    >
                        ×
                    </button>
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ color: COLORS.light, fontSize: '14px', marginBottom: '6px' }}>
                        {selectedEvent.title}
                    </div>
                    <div style={{ color: COLORS.muted, fontSize: '13px' }}>
                        {selectedEvent.date}, {selectedEvent.time}
                    </div>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ color: COLORS.muted, fontSize: '13px', marginBottom: '8px' }}>
                        Notify me:
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                    }}>
                        {reminderOptions.map((option, index) => (
                            <button 
                                key={index}
                                onClick={() => handleSetReminder(selectedEvent.id, option.value)}
                                            style={{
                                                background: COLORS.bgCard,
                                                border: `1px solid ${COLORS.border}`,
                                                borderRadius: '8px',
                                                color: COLORS.light,
                                                cursor: 'pointer',
                                                padding: '10px 14px',
                                                textAlign: 'left',
                                                fontSize: '13px',
                                                transition: 'all 0.2s ease',
                                                ':hover': {
                                                    background: COLORS.bgHover,
                                                    borderColor: COLORS.borderAccent
                                                }
                                            }}
                            >
                                {option.text}
                            </button>
                        ))}
                    </div>
                </div>
                
                <button 
                    onClick={() => setShowReminderPopup(false)}
                    style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${COLORS.primary}`,
                        borderRadius: '4px',
                        color: COLORS.primary,
                        cursor: 'pointer',
                        padding: '6px',
                        width: '100%',
                        fontSize: '13px'
                    }}
                >
                    Cancel
                </button>
            </div>
        );
    };
    
    // Date picker component
    const DatePicker = () => {
        const [month, setMonth] = useState(selectedDate.getMonth());
        const [year, setYear] = useState(selectedDate.getFullYear());
        
        // Get number of days in month
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        // Get first day of month (0 = Sunday, 1 = Monday, etc.)
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        
        // Generate array of days for the month
        const days = [];
        
        // Add empty cells for days before first day of month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(null);
        }
        
        // Add days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }
        
        // Previous month
        const prevMonth = () => {
            if (month === 0) {
                setMonth(11);
                setYear(year - 1);
            } else {
                setMonth(month - 1);
            }
        };
        
        // Next month
        const nextMonth = () => {
            if (month === 11) {
                setMonth(0);
                setYear(year + 1);
            } else {
                setMonth(month + 1);
            }
        };
        
        // Select a date
        const selectDate = (day) => {
            if (day === null) return;
            const newDate = new Date(year, month, day);
            handleDateChange(newDate);
        };
        
        // Check if a day is today
        const isToday = (day) => {
            if (day === null) return false;
            const today = new Date();
            return day === today.getDate() && 
                  month === today.getMonth() &&
                  year === today.getFullYear();
        };
        
        // Check if a day is selected
        const isSelected = (day) => {
            if (day === null) return false;
            return day === selectedDate.getDate() && 
                  month === selectedDate.getMonth() &&
                  year === selectedDate.getFullYear();
        };

        return (
            <div style={{
                background: COLORS.bgTertiary,
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                padding: '14px',
                position: 'absolute',
                zIndex: 10,
                top: '45px',
                left: '0',
                width: '300px',
                border: `1px solid ${COLORS.border}`
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                }}>
                    <button 
                        onClick={prevMonth}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: COLORS.muted,
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        &lt;
                    </button>
                    <div style={{ color: COLORS.light, fontSize: '14px' }}>
                        {new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </div>
                    <button 
                        onClick={nextMonth}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: COLORS.muted,
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        &gt;
                    </button>
                </div>
                
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '1px'
                }}>
                    {/* Day headers */}
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, index) => (
                        <div key={index} style={{
                            padding: '4px',
                            textAlign: 'center',
                            color: COLORS.muted,
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}>
                            {day}
                        </div>
                    ))}
                    
                    {/* Calendar days */}
                    {days.map((day, index) => (
                        <div 
                            key={index}
                            onClick={() => day !== null && selectDate(day)}
                            style={{
                                padding: '4px',
                                textAlign: 'center',
                                cursor: day !== null ? 'pointer' : 'default',
                                color: day === null ? 'transparent' : 
                                       isToday(day) ? COLORS.info : 
                                       isSelected(day) ? '#fff' : COLORS.muted,
                                backgroundColor: isSelected(day) ? COLORS.primary : 'transparent',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '1px auto',
                                fontSize: '12px',
                                fontWeight: isToday(day) || isSelected(day) ? 'bold' : 'normal'
                            }}
                        >
                            {day}
                        </div>
                    ))}
                </div>
                
                <div style={{
                    textAlign: 'right',
                    marginTop: '8px'
                }}>
                    <button 
                        onClick={() => setShowDatePicker(false)}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: COLORS.primary,
                            cursor: 'pointer',
                            fontSize: '13px',
                            padding: '4px 8px'
                        }}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={() => handleDateChange(new Date())}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: COLORS.primary,
                            cursor: 'pointer',
                            fontSize: '13px',
                            padding: '4px 8px',
                            marginLeft: '8px'
                        }}
                    >
                        Today
                    </button>
                </div>
            </div>
        );
    };
    
    // Format date as "Day, Month Date, Year"
    const formattedDate = activeTab === 'agenda' 
        ? currentDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        : selectedDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });

    return (
        <PanelContent>
            <PanelTitle><FaCalendarAlt /> Upcoming Sessions & Reminders</PanelTitle>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                width: '100%',
                height: 'calc(100% - 26px)',
                overflowY: 'auto',
                position: 'relative' // Add relative positioning for absolute positioned popups
            }}>
                <div style={{
                    padding: '0 0 10px 0',
                    borderBottom: `1px solid ${COLORS.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'relative'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div 
                            style={{
                                color: COLORS.light,
                                fontSize: window.innerWidth <= 1920 ? '15px' : '18px',
                                fontWeight: '600',
                                cursor: activeTab === 'day' ? 'pointer' : 'default'
                            }}
                            onClick={() => activeTab === 'day' && setShowDatePicker(!showDatePicker)}
                        >
                            {formattedDate}
                            {activeTab === 'day' && (
                                <span style={{ marginLeft: '6px', color: COLORS.primary }}>
                                    ▼
                                </span>
                            )}
                        </div>
                        <div style={{
                            color: COLORS.primary,
                            fontSize: window.innerWidth <= 1920 ? '13px' : '15px',
                            marginTop: '2px',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <FaClock style={{ 
                                fontSize: window.innerWidth <= 1920 ? '10px' : '12px', 
                                marginRight: '4px',
                                color: COLORS.primary
                            }} />
                            {currentTime}
                        </div>
                    </div>
                    <div style={{
                        display: 'flex',
                        gap: '5px'
                    }}>
                        <button 
                            style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: activeTab === 'day' ? COLORS.primary : COLORS.muted,
                                cursor: 'pointer',
                                fontSize: window.innerWidth <= 1920 ? '13px' : '15px',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                                borderBottom: activeTab === 'day' ? `2px solid ${COLORS.primary}` : 'none'
                            }}
                            onClick={() => handleTabChange('day')}
                        >
                            <FaClock style={{ marginRight: '4px' }} /> Day
                        </button>
                        <button 
                            style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: activeTab === 'agenda' ? COLORS.primary : COLORS.muted,
                                cursor: 'pointer',
                                fontSize: window.innerWidth <= 1920 ? '13px' : '15px',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                                borderBottom: activeTab === 'agenda' ? `2px solid ${COLORS.primary}` : 'none'
                            }}
                            onClick={() => handleTabChange('agenda')}
                        >
                            <FaCalendarAlt style={{ marginRight: '4px' }} /> Agenda
                        </button>
                        <button 
                            style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: COLORS.muted,
                                cursor: 'pointer',
                                fontSize: window.innerWidth <= 1920 ? '13px' : '15px',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                            }}
                            onClick={refreshEvents}
                            title="Refresh events"
                        >
                            <svg 
                                width="14" 
                                height="14" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                style={{ marginRight: '4px' }}
                            >
                                <path d="M23 4v6h-6"/>
                                <path d="M1 20v-6h6"/>
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                                <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                            </svg>
                            Refresh
                        </button>
                    </div>
                    
                    {showDatePicker && <DatePicker />}
                </div>
                
                <div style={{
                    marginTop: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: window.innerWidth <= 1920 ? '10px' : '12px',
                    overflowY: 'auto',
                    flex: 1
                }}>
                    {isLoading ? (
                        <div style={{
                            color: COLORS.light,
                            textAlign: 'center',
                            padding: '20px',
                            fontSize: window.innerWidth <= 1920 ? '14px' : '16px'
                        }}>
                            Loading events...
                        </div>
                    ) : upcomingEvents.length > 0 ? upcomingEvents.map((item) => (
                        <div key={item.id} style={{
                            background: COLORS.bgCard,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '10px',
                            padding: window.innerWidth <= 1920 ? '10px 12px' : '12px 14px',
                            borderLeft: item.status === 'in-progress' ? `4px solid ${COLORS.success}` : 
                                       item.status === 'finished' ? `4px solid ${COLORS.danger}` :
                                       item.isReady ? `4px solid ${COLORS.warning}` : `4px solid ${COLORS.primary}`,
                            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            ':hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                            }
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '6px'
                            }}>
                                <div style={{
                                    color: COLORS.light,
                                    fontWeight: '600',
                                    fontSize: window.innerWidth <= 1920 ? '14px' : '16px'
                                }}>
                                    {item.title}
                                </div>
                                <div style={{
                                    backgroundColor: item.status === 'in-progress' ? 'rgba(92, 184, 92, 0.25)' : 
                                                   item.status === 'finished' ? 'rgba(217, 83, 79, 0.25)' :
                                                   item.isReady ? 'rgba(240, 173, 78, 0.25)' :
                                                   'rgba(53, 124, 210, 0.25)',
                                    color: item.status === 'in-progress' ? COLORS.success : 
                                           item.status === 'finished' ? COLORS.danger :
                                           item.isReady ? COLORS.warning :
                                           COLORS.primary,
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    fontSize: window.innerWidth <= 1920 ? '10px' : '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontWeight: '500'
                                }}>
                                    <FaClock style={{ fontSize: window.innerWidth <= 1920 ? '9px' : '10px', marginRight: '4px' }} />
                                    {activeTab === 'agenda' && (
                                        <span style={{ marginRight: '5px' }}>{item.date}</span>
                                    )}
                                    {item.time}
                                </div>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <div style={{
                                    color: COLORS.muted,
                                    fontSize: window.innerWidth <= 1920 ? '12px' : '14px'
                                }}>
                                    {item.client}
                                    {item.location && ` | ${item.location}`}
                                </div>
                                <div style={{
                                    display: 'flex',
                                    gap: '8px'
                                }}>
                                    <button style={{
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: item.status === 'in-progress' ? COLORS.success : 
                                               item.status === 'finished' ? COLORS.danger :
                                               item.isReady ? COLORS.warning : 
                                               COLORS.muted,
                                        cursor: 'pointer',
                                        fontSize: window.innerWidth <= 1920 ? '11px' : '13px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0',
                                        fontWeight: '500'
                                    }}>
                                        <FaCheckCircle style={{ marginRight: '4px' }} /> 
                                        {item.status === 'in-progress' ? 'In Progress' : 
                                         item.status === 'finished' ? 'Finished' :
                                         item.isReady ? 'Ready to Join' : 'Not Ready'}
                                    </button>
                                    <button 
                                        onClick={() => openReminderPopup(item)}
                                        style={{
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            color: hasReminder(item.id) ? COLORS.success : COLORS.muted,
                                            cursor: 'pointer',
                                            fontSize: window.innerWidth <= 1920 ? '11px' : '13px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '0',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <FaBell style={{ marginRight: '4px' }} /> 
                                        {hasReminder(item.id) ? getReminderText(item.id) : 'Remind'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )) : (
                        <div style={{
                            color: COLORS.muted,
                            textAlign: 'center',
                            padding: '20px',
                            fontSize: window.innerWidth <= 1920 ? '14px' : '16px'
                        }}>
                            {activeTab === 'agenda' 
                                ? 'No upcoming sessions scheduled' 
                                : `No sessions scheduled for ${selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
                            }
                            <div style={{
                                marginTop: '10px'
                            }}>
                                <button 
                                    onClick={refreshEvents}
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                                        color: COLORS.primaryLight,
                                        border: `1px solid ${COLORS.borderAccent}`,
                                        borderRadius: '8px',
                                        padding: '8px 14px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <svg 
                                        width="14" 
                                        height="14" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    >
                                        <path d="M23 4v6h-6"/>
                                        <path d="M1 20v-6h6"/>
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                                        <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                    </svg>
                                    Try Refreshing
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                
                {showReminderPopup && <ReminderPopup />}
            </div>
        </PanelContent>
    );
};

export default UpcomingSessions;
